'use client';

import { useEffect, useMemo, useState } from 'react';
import { Check } from 'lucide-react';

import {
  buildPricingPlans,
  type PricingPlan,
  resolvePricingPlanSlug,
  type PlanSlug,
  type SelectablePlanSlug,
} from '@/app/pricing/pricing-plans';
import { RecurringConsentBlock } from '@/components/billing/recurring-consent-block';
import { PublicLink } from '@/components/PublicLink';
import { Button } from '@/components/ui/button';
import { AnimatedContainer } from '@/components/ui/animated-container';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAnimation } from '@/contexts/AnimationContext';
import { usePublicSession } from '@/hooks/use-public-session';
import { useBillingActions } from '@/hooks/use-billing-actions';
import type { IndexedPublicPlanPrices } from '@/lib/public-plan-prices-snapshot';
import {
  formatPublicDecimalAmount,
  formatPublicPlanCadence,
  formatPublicPlanPrice,
} from '@/lib/public-plan-prices';
import { cn } from '@/lib/utils';
import type { PublicPlanPrice, SubscriptionInfo } from '@/types/billing';
import {
  PageHero,
  PageIntro,
  PageSection,
  SectionPill,
  SurfacePanel,
} from '@/components/site/page-primitives';

interface PricingPageClientProps {
  initialPublicPlanPrices: IndexedPublicPlanPrices;
}

export default function PricingPageClient({ initialPublicPlanPrices }: PricingPageClientProps) {
  const { enableAnimations } = useAnimation();
  const { user, isAuthenticated } = usePublicSession();
  const { startCheckout } = useBillingActions();
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);
  const [pendingPlan, setPendingPlan] = useState<SelectablePlanSlug | null>(null);
  const [recurringConsentPlan, setRecurringConsentPlan] = useState<'basic' | 'pro' | null>(null);
  const [hasRecurringConsent, setHasRecurringConsent] = useState(false);
  const publicPlanPrices = initialPublicPlanPrices;

  useEffect(() => {
    if (!isAuthenticated) {
      setSubscription(null);
      setSubscriptionError(null);
      return;
    }

    let cancelled = false;

    const loadSubscription = async () => {
      setSubscriptionError(null);

      try {
        const response = await fetch('/api/billing/subscription', { cache: 'no-store' });
        if (cancelled) {
          return;
        }

        if (!response.ok) {
          const message =
            response.status === 401
              ? 'Войдите снова, чтобы управлять оплатой.'
              : 'Сейчас не удалось загрузить ваш текущий тариф.';
          setSubscription(null);
          setSubscriptionError(message);
          return;
        }

        const payload = (await response.json()) as SubscriptionInfo | null;
        setSubscription(payload);
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load subscription summary', error);
          setSubscription(null);
          setSubscriptionError('Сейчас не удается загрузить данные по оплате.');
        }
      }
    };

    loadSubscription();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const currentPlanSlug = useMemo<PlanSlug | undefined>(() => {
    return resolvePricingPlanSlug(subscription?.plan_slug) ?? resolvePricingPlanSlug(user?.plan);
  }, [subscription?.plan_slug, user?.plan]);

  const isPendingVerification = subscription?.status === 'pending_verification';
  const isAccountBlocked = subscription?.status === 'blocked_duplicate';

  type PlanAction =
    | { type: 'link'; label: string; href: string; disabled?: boolean }
    | {
        type: 'checkout';
        label: string;
        plan: SelectablePlanSlug;
        mode?: 'subscription' | 'setup';
        disabled?: boolean;
      };

  const launchCheckout = async (
    planSlug: SelectablePlanSlug,
    mode?: 'subscription' | 'setup',
    source?: 'pricing',
  ) => {
    setPendingPlan(planSlug);
    try {
      await startCheckout({
        plan: planSlug,
        mode,
        recurring_consent: source ? { accepted: true, source } : undefined,
      });
    } catch {
      setPendingPlan(null);
    }
  };

  const handleCheckout = async (planSlug: SelectablePlanSlug, mode?: 'subscription' | 'setup') => {
    if (pendingPlan) {
      return;
    }

    const resolvedMode = mode ?? (planSlug === 'hobby' ? 'setup' : 'subscription');
    if (resolvedMode === 'subscription' && planSlug !== 'hobby') {
      setRecurringConsentPlan(planSlug);
      setHasRecurringConsent(false);
      return;
    }

    await launchCheckout(planSlug, resolvedMode);
  };

  const handleRecurringDialogChange = (open: boolean) => {
    if (pendingPlan) {
      return;
    }
    if (!open) {
      setRecurringConsentPlan(null);
      setHasRecurringConsent(false);
    }
  };

  const handleConfirmRecurringCheckout = async () => {
    if (!recurringConsentPlan || pendingPlan) {
      return;
    }

    await launchCheckout(recurringConsentPlan, 'subscription', 'pricing');
  };

  const getPlanAction = (plan: PricingPlan): PlanAction => {
    if (plan.slug === 'custom') {
      return { type: 'link', label: plan.buttonText, href: plan.buttonLink };
    }

    if (!isAuthenticated) {
      return { type: 'link', label: plan.buttonText, href: plan.buttonLink };
    }

    if (plan.slug === 'hobby') {
      if (currentPlanSlug === 'hobby' && isPendingVerification) {
        return { type: 'checkout', label: 'Подтвердить карту', plan: 'hobby', mode: 'setup' };
      }

      if (currentPlanSlug === 'hobby') {
        return { type: 'link', label: 'Текущий тариф', href: '#', disabled: true };
      }

      return {
        type: 'link',
        label: 'Понизить в разделе оплаты',
        href: '/billing',
        disabled: isAccountBlocked,
      };
    }

    const selectableSlug = plan.slug as SelectablePlanSlug;
    if (isAccountBlocked) {
      return { type: 'link', label: 'Аккаунт заблокирован', href: '#', disabled: true };
    }

    if (currentPlanSlug === selectableSlug) {
      return { type: 'link', label: 'Текущий тариф', href: '#', disabled: true };
    }

    return {
      type: 'checkout',
      label: plan.buttonText,
      plan: selectableSlug,
    };
  };

  const policySections = [
    {
      title: 'Что считается запросом',
      points: [
        'Ошибки аутентификации никогда не тарифицируются.',
        'Если мы возвращаем ошибку 5xx до завершения, такой запуск не тарифицируется.',
        'Таймаут на стороне клиента тарифицируется, если извлечение позже успешно завершается на нашей стороне.',
      ],
    },
    {
      title: 'Лимиты и параллельные запуски',
      points: [
        'Лимит параллельных запусков определяет, сколько извлечений может идти одновременно.',
        'Когда лимит параллельных запусков исчерпан, новые задания получают HTTP 429 с заголовком Retry-After.',
        'Параллельные запуски влияют на общую пропускную способность, а не на скорость одного запроса (обычно около 10 с, максимум до 60 с).',
      ],
    },
    {
      title: 'Карты и защита от злоупотреблений',
      points: [
        'Для тарифа Хобби нужна действующая карта для проверки, но списаний по нему нет.',
        'На тарифе Хобби использование останавливается на 100 запросах, перерасход недоступен.',
        'Подтверждение адреса электронной почты и лимиты по IP защищают платформу от злоупотреблений.',
      ],
    },
    {
      title: 'Выбор прокси по стране',
      points: [
        'Доступно только на тарифах Про и Индивидуальный.',
        'Список доступных стран может меняться в зависимости от состояния пула.',
      ],
    },
    {
      title: 'Смена тарифов и расчетные периоды',
      points: [
        'Повышение тарифа сразу списывает стоимость полного месяца по новому тарифу и переносит дату продления.',
        'Понижение тарифа планируется на следующее продление, а функции более высокого тарифа остаются активны до этой даты.',
        'Использование всегда тарифицируется по тарифу, который был активен на момент выполнения запроса. Старые запросы не пересчитываются.',
      ],
    },
    {
      title: 'Неоплаченные счета',
      points: [
        'Если автопродление не проходит, мы помечаем тариф как требующий оплаты и предлагаем провести оплату заново.',
        'После успешной оплаты новый расчетный период активируется сразу.',
        'Как только счета оплачены, мы сразу восстанавливаем сервис и снимаем блокировки.',
      ],
    },
  ];

  const billingExamples = useMemo(() => {
    const basicPrice = publicPlanPrices.basic;
    const proPrice = publicPlanPrices.pro;

    const buildPaidExample = (price: PublicPlanPrice, totalRequests: number) => {
      const extraRequests = Math.max(totalRequests - price.included_units, 0);
      const overageAmount = extraRequests * price.overage_price;
      return {
        included: `${price.included_units.toLocaleString('ru-RU')} включено`,
        overage:
          extraRequests > 0
            ? `${extraRequests.toLocaleString('ru-RU')} × ${formatPublicDecimalAmount(price.overage_price, price.currency)} = ${formatPublicDecimalAmount(overageAmount, price.currency)}`
            : 'Перерасход отсутствует',
        total:
          extraRequests > 0
            ? `${formatPublicPlanPrice(price)} подписка + ${formatPublicDecimalAmount(overageAmount, price.currency)} перерасход = ${formatPublicDecimalAmount(price.amount_cents / 100 + overageAmount, price.currency)}`
            : `${formatPublicPlanPrice(price)} за период`,
      };
    };

    return [
      {
        scenario: 'Базовый • 12 000 запросов',
        ...buildPaidExample(basicPrice, 12000),
      },
      {
        scenario: 'Про • 60 000 запросов',
        ...buildPaidExample(proPrice, 60000),
      },
      {
        scenario: 'Хобби',
        included: '100 включено',
        overage: 'Недоступно',
        total: 'Жесткий лимит 100 запросов в месяц',
      },
    ];
  }, [publicPlanPrices]);

  const planChangeHighlights = [
    {
      title: 'Повышение в середине цикла',
      description:
        'Повышение вступает в силу сразу, открывает больше воркеров и запускает новый расчетный месяц. Мы списываем полную стоимость нового тарифа в день повышения.',
    },
    {
      title: 'Понижение на продлении',
      description:
        'Понижение тарифа планируется на следующее продление. До этой даты вы сохраняете возможности текущего тарифа, а затем переходите на более низкий тариф с новой квотой.',
    },
    {
      title: 'Раздельная тарификация использования',
      description:
        'Каждый запрос оценивается по тарифу, который был активен в момент выполнения. Высокая нагрузка до повышения никогда не пересчитывается по более дешевому тарифу.',
    },
  ];

  const faqItems = [
    {
      question: 'Тарифицируются ли неуспешные запросы?',
      answer:
        'Ошибки аутентификации и извлечения, которые завершаются ошибкой 5xx до окончания обработки, никогда не тарифицируются. Если клиент получил таймаут, но извлечение позже успешно завершилось на нашей стороне, такой запрос все равно будет засчитан в лимит или перерасход.',
    },
    {
      question: 'Что происходит, когда исчерпан лимит параллельных запусков?',
      answer:
        'Лимит параллельных запусков определяет, сколько извлечений может выполняться одновременно. Когда этот лимит исчерпан, мы возвращаем HTTP 429 Too Many Requests вместе с заголовком Retry-After, чтобы вы могли снизить нагрузку или поставить задания в очередь.',
    },
    {
      question: 'Нужна ли карта для тарифа Хобби?',
      answer:
        'Да. Мы просим действующую карту только для проверки. На тарифе Хобби списаний нет, а использование останавливается на 100 запросах в месяц без перерасхода.',
    },
    {
      question: 'Кому доступен выбор прокси по стране?',
      answer:
        'Выбор прокси по стране доступен на тарифах Про и Индивидуальный. Список поддерживаемых стран может меняться по мере балансировки пула прокси.',
    },
    {
      question: 'Что происходит при повышении тарифа в середине цикла?',
      answer:
        'Повышение сразу открывает новые возможности, увеличивает лимит параллельных запусков, завершает предыдущий расчетный период и списывает полную месячную стоимость более высокого тарифа. Следующее продление переносится на месяц вперед от даты повышения.',
    },
    {
      question: 'Когда вступает в силу понижение тарифа?',
      answer:
        'Понижение тарифа планируется на конец текущего расчетного периода. До даты продления сохраняются лимиты более высокого тарифа, после чего начнется новый период уже на более низком тарифе.',
    },
    {
      question: 'Как вы обрабатываете неоплаченные счета?',
      answer:
        'Если автопродление не проходит, мы переводим аккаунт в статус, требующий оплаты, и просим провести оплату заново. После успешного платежа доступ сразу восстанавливается и начинается новый оплаченный период.',
    },
    {
      question: 'Как сбрасывается лимит Хобби, если он не идет через подписку провайдера?',
      answer:
        'Тариф Хобби отслеживается внутри S-SHOT. Мы сбрасываем лимит в 100 запросов каждый месяц на нашей стороне и автоматически снимаем блокировку в начале следующего цикла или сразу после повышения тарифа.',
    },
  ];

  const plans = useMemo(
    () => buildPricingPlans({ isAuthenticated, publicPlanPrices }),
    [isAuthenticated, publicPlanPrices],
  );

  const recurringConsentDetails = recurringConsentPlan
    ? publicPlanPrices[recurringConsentPlan]
    : null;

  return (
    <>
      <PageHero>
        <AnimatedContainer
          animation="none"
          className="mx-auto flex w-full max-w-4xl flex-col gap-8 text-center"
        >
          <PageIntro
            align="center"
            eyebrow="Тарифы"
            title="Тарифы для парсинга сайтов — от тестов до рабочих проектов."
            description="На всех тарифах работает один движок парсинга и одинаковый формат данных. Отличаются лимиты запросов, поддержка и дополнительные возможности."
            compact
            actions={
              <div className="flex flex-wrap justify-center gap-2">
                <SectionPill>Ежемесячная оплата</SectionPill>
                <SectionPill>Один движок извлечения на всех тарифах</SectionPill>
                <SectionPill>Использование тарифицируется по активному тарифу</SectionPill>
              </div>
            }
          />
        </AnimatedContainer>
      </PageHero>
      <PageSection className="gap-8">
        <AnimatedContainer
          animation="none"
          className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4"
        >
          {plans.map((plan) => {
            const showsMonthlySuffix = Boolean(plan.priceSuffix);
            const detailItems = [
              { label: 'Включенные запросы', value: plan.includedRequests },
              { label: 'Перерасход', value: plan.overage },
              { label: 'Параллельные запуски', value: plan.workers },
              { label: 'Поддержка', value: plan.support },
            ];
            const action = getPlanAction(plan);
            const selectableSlug = plan.slug === 'custom' ? null : (plan.slug as SelectablePlanSlug);
            const isProcessing = selectableSlug ? pendingPlan === selectableSlug : false;
            const isOtherCheckoutLocked =
              action.type === 'checkout' &&
              selectableSlug !== null &&
              pendingPlan !== null &&
              pendingPlan !== selectableSlug;
            const buttonDisabled = Boolean(action.disabled) || isProcessing || isOtherCheckoutLocked;
            const buttonLabel = isProcessing ? 'Переадресация...' : action.label;
            const buttonClassName = plan.highlighted
              ? 'h-11 w-full bg-[#1c2c5a] text-white shadow-md hover:bg-[#24376e]'
              : buttonDisabled
                ? 'h-11 w-full border-none bg-[color:rgba(23,38,59,0.12)] text-[var(--site-ink)] hover:bg-[color:rgba(23,38,59,0.14)]'
                : 'h-11 w-full border-[color:var(--site-border-soft)] bg-white text-[var(--site-ink)] hover:bg-[var(--site-surface-inset)]';
            const buttonVariant = plan.highlighted ? 'default' : 'outline';

            const renderButton = () => {
              if (action.type === 'checkout') {
                return (
                  <Button
                    className={buttonClassName}
                    variant={buttonVariant}
                    disabled={buttonDisabled}
                    onClick={buttonDisabled ? undefined : () => handleCheckout(action.plan, action.mode)}
                  >
                    {buttonLabel}
                  </Button>
                );
              }

              if (!buttonDisabled) {
                return (
                  <PublicLink href={action.href}>
                    <Button className={buttonClassName} variant={buttonVariant}>
                      {buttonLabel}
                    </Button>
                  </PublicLink>
                );
              }

              return (
                <Button className={buttonClassName} variant={buttonVariant} disabled>
                  {buttonLabel}
                </Button>
              );
            };

            return (
              <SurfacePanel
                key={plan.slug}
                className={cn(
                  'h-full gap-0 overflow-hidden p-0 text-left',
                  plan.highlighted
                    ? 'border-[color:rgba(42,92,150,0.4)] shadow-[0_20px_52px_rgba(23,38,59,0.1)]'
                    : 'bg-[color:rgba(255,255,255,0.76)]',
                )}
              >
                <div
                  className={cn(
                    'flex flex-col gap-3 px-6 py-6',
                    plan.highlighted
                      ? 'bg-[linear-gradient(180deg,rgba(12,113,147,0.96),rgba(24,44,90,0.98))] text-white'
                      : 'border-b border-[color:rgba(23,38,59,0.08)] bg-[color:rgba(255,255,255,0.82)] text-[var(--site-ink-strong)]',
                  )}
                >
                  <div className="text-xs font-semibold uppercase tracking-[0.3em] opacity-80">
                    {plan.name}
                  </div>
                  <div className="flex flex-wrap items-baseline gap-1">
                        <span className="text-4xl font-extrabold">{plan.price}</span>
                        {showsMonthlySuffix ? (
                          <>
                            <span className="text-base opacity-80 whitespace-nowrap">{plan.priceSuffix}</span>
                          </>
                        ) : null}
                  </div>
                  <p
                    className={cn(
                      'text-sm',
                      plan.highlighted ? 'text-[#dbe6ff]' : 'text-[var(--site-ink-muted)]',
                    )}
                  >
                    {plan.description}
                  </p>
                </div>

                <div className="flex h-full flex-col gap-5 px-6 py-6">
                  <dl className="text-sm divide-y divide-slate-200/90">
                    {detailItems.map((item) => (
                      <div
                        key={item.label}
                        className="flex items-start justify-between gap-3 py-3 first:pt-0 last:pb-0"
                      >
                        <dt className="text-[var(--site-ink-muted)]">{item.label}</dt>
                        <dd className="text-right font-semibold text-[#1e2a3b] whitespace-normal break-words">
                          {item.value}
                        </dd>
                      </div>
                    ))}
                  </dl>

                  {plan.extras.length > 0 ? (
                    <div>
                      <div className="flex items-center gap-1 text-sm font-semibold text-[var(--site-ink)]">
                        <Check size={16} className="text-green-500" />
                        Ключевые возможности
                      </div>
                      <ul className="mt-2 space-y-2 text-sm">
                        {plan.extras.map((extra) => (
                          <li key={`${plan.name}-${extra}`} className="flex items-start">
                            <Check size={16} className="mr-2 mt-0.5 flex-shrink-0 text-green-500" />
                            <span>{extra}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {plan.notice ? <p className="text-xs text-[var(--site-ink-muted)]">{plan.notice}</p> : null}

                  <div className="mt-auto pt-2">{renderButton()}</div>
                </div>
              </SurfacePanel>
            );
          })}
        </AnimatedContainer>

        {subscriptionError ? (
          <p className="text-center text-sm text-red-600">{subscriptionError}</p>
        ) : null}
      </PageSection>

      <PageSection mobileStyle="band" className="site-section-spotlight gap-6">
        <AnimatedContainer
          animation={enableAnimations ? 'fadeIn' : 'none'}
          delay={0.1}
          className="grid gap-4 max-sm:gap-0 max-sm:divide-y max-sm:divide-[color:var(--site-border-soft)] lg:grid-cols-3"
        >
          {planChangeHighlights.map((highlight) => (
            <SurfacePanel
              key={highlight.title}
              phoneStyle="open"
              className="gap-3 border-[color:rgba(72,114,164,0.16)] bg-[color:rgba(234,244,252,0.8)] max-sm:py-4"
            >
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#355c96]">
                {highlight.title}
              </p>
              <p className="text-sm leading-relaxed text-[#0b1736]">{highlight.description}</p>
            </SurfacePanel>
          ))}
        </AnimatedContainer>
      </PageSection>

      <PageSection mobileStyle="band" className="gap-8">
        <AnimatedContainer
          animation={enableAnimations ? 'fadeIn' : 'none'}
          className="mx-auto flex max-w-3xl flex-col gap-3 text-center"
        >
          <h2 className="text-3xl font-semibold tracking-[-0.03em] text-[var(--site-ink-strong)]">
            Как работают лимиты и оплата
          </h2>
          <p className="site-copy mx-auto max-w-3xl">
            Эти правила повторяют реальное поведение подписки, чтобы вы заранее понимали, как считаются запросы, лимиты параллельных запусков и защитные механизмы.
          </p>
        </AnimatedContainer>

        <AnimatedContainer
          animation={enableAnimations ? 'slideUp' : 'none'}
          className="grid gap-6 max-sm:gap-0 max-sm:divide-y max-sm:divide-[color:var(--site-border-soft)] lg:grid-cols-2"
        >
          {policySections.map((section) => (
            <SurfacePanel key={section.title} phoneStyle="open" className="h-full max-sm:py-4">
              <h3 className="text-lg font-semibold text-[#1e2a3b]">{section.title}</h3>
              <ul className="space-y-2 text-sm text-[var(--site-ink)]">
                {section.points.map((point) => (
                  <li key={point} className="flex items-start">
                    <Check size={16} className="mr-2 mt-0.5 flex-shrink-0 text-green-500" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </SurfacePanel>
          ))}
        </AnimatedContainer>

        <AnimatedContainer animation={enableAnimations ? 'slideUp' : 'none'} delay={0.1}>
          <SurfacePanel phoneStyle="open" className="gap-5 max-sm:border-t max-sm:border-[color:var(--site-border-soft)] max-sm:pt-4">
            <div>
              <h3 className="text-lg font-semibold text-[#1e2a3b]">Примеры оплаты</h3>
              <p className="mt-1 text-sm text-[var(--site-ink)]">
                Сначала списывается включенный объем, затем перерасход тарифицируется по опубликованной ставке.
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-[var(--site-ink-muted)]">
                    <th className="py-3 pr-4">Сценарий</th>
                    <th className="py-3 pr-4">Включено</th>
                    <th className="py-3 pr-4">Перерасход</th>
                    <th className="py-3 pr-4">Итог за месяц</th>
                  </tr>
                </thead>
                <tbody>
                  {billingExamples.map((example) => (
                    <tr key={example.scenario} className="border-b last:border-b-0">
                      <td className="py-3 pr-4 font-medium text-[#1e2a3b]">{example.scenario}</td>
                      <td className="py-3 pr-4 text-[var(--site-ink)]">{example.included}</td>
                      <td className="py-3 pr-4 text-[var(--site-ink)]">{example.overage}</td>
                      <td className="py-3 pr-4 font-semibold text-[var(--site-ink-strong)]">{example.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SurfacePanel>
        </AnimatedContainer>
      </PageSection>

      <PageSection mobileStyle="band" className="gap-8">
        <AnimatedContainer
          animation={enableAnimations ? 'fadeIn' : 'none'}
          className="mx-auto flex max-w-3xl flex-col gap-3 text-center"
        >
          <h2 className="text-3xl font-semibold tracking-[-0.03em] text-[var(--site-ink-strong)]">
            Частые вопросы
          </h2>
          <p className="site-copy mx-auto max-w-3xl">
            Есть вопросы о тарифах, оплате или повышении? Здесь собраны основные пограничные случаи.
          </p>
        </AnimatedContainer>

        <div className="grid gap-4 max-sm:gap-0 max-sm:divide-y max-sm:divide-[color:var(--site-border-soft)] lg:grid-cols-2">
          {faqItems.map((item, index) => (
            <AnimatedContainer
              key={item.question}
              animation={enableAnimations ? 'slideUp' : 'none'}
              delay={0.06 * (index + 1)}
              className="h-full"
            >
              <SurfacePanel phoneStyle="open" className="h-full max-sm:py-4">
                <h3 className="text-lg font-semibold text-[var(--site-ink-strong)]">{item.question}</h3>
                <p className="text-[var(--site-ink)]">{item.answer}</p>
              </SurfacePanel>
            </AnimatedContainer>
          ))}
        </div>

        <AnimatedContainer
          animation={enableAnimations ? 'fadeIn' : 'none'}
          delay={0.35}
          className="flex flex-col items-center gap-4 text-center"
        >
          <p className="max-w-2xl text-[var(--site-ink)]">
            Остались вопросы по тарифам, оплате или индивидуальному запуску?
          </p>
          <PublicLink href="/contact">
            <Button
              variant="outline"
              className="border-[var(--site-accent-text)] text-[var(--site-accent-text)] hover:bg-[#eef2f7]"
            >
              Связаться с продажами
            </Button>
          </PublicLink>
        </AnimatedContainer>
      </PageSection>

      <Dialog open={Boolean(recurringConsentPlan)} onOpenChange={handleRecurringDialogChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Подтвердите автосписания</DialogTitle>
            <DialogDescription>
              Перед запуском платного тарифа нужно отдельно подтвердить согласие на ежемесячные автоматические списания по условиям оферты.
            </DialogDescription>
          </DialogHeader>
          {recurringConsentDetails ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-[color:var(--site-border-soft)] p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-1">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[var(--site-ink-muted)]">
                      Выбранный тариф
                    </p>
                    <p className="text-sm font-semibold text-[var(--site-ink-strong)]">
                      {recurringConsentDetails.plan_name}
                    </p>
                  </div>
                  <p className="text-sm font-semibold text-[var(--site-ink-strong)] whitespace-nowrap">
                    {formatPublicPlanPrice(recurringConsentDetails)} / месяц
                  </p>
                </div>
              </div>

              <RecurringConsentBlock
                checked={hasRecurringConsent}
                onCheckedChange={setHasRecurringConsent}
                amountLabel={formatPublicPlanPrice(recurringConsentDetails)}
                frequencyLabel={formatPublicPlanCadence(recurringConsentDetails)}
                checkboxId="pricing-recurring-consent"
              />

              <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-end">
                <Button variant="outline" onClick={() => handleRecurringDialogChange(false)} disabled={pendingPlan !== null}>
                  Не сейчас
                </Button>
                <Button
                  onClick={handleConfirmRecurringCheckout}
                  disabled={!hasRecurringConsent || pendingPlan !== null}
                >
                  {pendingPlan && recurringConsentPlan === pendingPlan ? 'Переадресация...' : 'Перейти к оплате'}
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
